import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { Airport } from "../../../types";
import { toast } from "react-toastify";
import { fetchAirports, deleteAirports, createAirport, updateAirport } from "./airportAsyncThunk";
import { IAuthResponse } from "../auth/IAuthState";
import { RequestResults } from "../../common/RequestStatus";

export interface DeleteAirports extends IAuthResponse {
  id: string;
}

interface AirportState {
  data: Airport[];
  status: RequestResults;
  loading: boolean;
  error: string | null;
}

const initialState: AirportState = {
  data: [],
  status: RequestResults.idle,
  loading: false,
  error: null,
};

// Slice
const airportSlice = createSlice({
  name: "airports",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAirports.pending, (state) => {
        state.status = RequestResults.pending;
        state.error = null;
      })
      .addCase(fetchAirports.fulfilled, (state, action: PayloadAction<Airport[]>) => {
        state.status = RequestResults.success;
        state.data = action.payload;
      })
      .addCase(fetchAirports.rejected, (state, action) => {
        state.status = RequestResults.failed;
        toast.error(action.payload || "Failed to fetch airports");
        state.error = action.payload || "Failed to fetch airports";
      });
    builder
      .addCase(createAirport.pending, (state) => {
        state.loading = true;
      })
      .addCase(createAirport.fulfilled, (state, action: PayloadAction<Airport>) => {
        state.loading = false;
        state.data.push(action.payload);
      })
      .addCase(createAirport.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to add Airport");
        state.error = action.payload || "Failed to add Airport";
      });
    builder
      .addCase(updateAirport.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAirport.fulfilled, (state, action: PayloadAction<Airport>) => {
        const index = state.data.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
        state.loading = false;
      })
      .addCase(updateAirport.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to update Airport");
        state.error = action.payload || "Failed to update Airport";
      });
    builder
      .addCase(deleteAirports.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteAirports.fulfilled, (state, action: PayloadAction<DeleteAirports>) => {
        const index = state.data.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.data = state.data.filter((item) => item.id !== action.payload.id);
        }
        state.loading = false;
      })
      .addCase(deleteAirports.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to delete Airport");
        state.error = action.payload || "Failed to delete Airport";
      });
  },
});

export const selectAirports = (state: RootState) => state.airports;

export default airportSlice.reducer;
